/**
 * Integrytas Group — EmptyState (common).
 *
 * Designed empty moments for list pages and dashboard panels (P-10).
 * Empty screens are disproportionately what new users and month-end
 * "done" states see — an undifferentiated "No records" reads as
 * malfunction. Three variants distinguish the cause:
 *
 *   'no-data'  — never populated: invite the first record (pass the page's
 *                "+ New" button as `action`).
 *   'no-match' — filters/search excluded everything: say so, and pass a
 *                clear-filters control as `action`.
 *   'all-clear'— a good empty (nothing needs attention): check icon,
 *                calm confirmation.
 *
 * The default (variant omitted) keeps the original neutral briefcase.
 * Titles/descriptions are caller-supplied, already-localized strings.
 */

export type EmptyStateVariant = 'no-data' | 'no-match' | 'all-clear';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  variant?: EmptyStateVariant;
}

function BriefcaseIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="mx-auto mb-3 h-12 w-12 text-csm-border-strong" stroke="currentColor" strokeWidth="1.5">
      <rect x="8" y="12" width="32" height="28" rx="2" />
      <path d="M16 12V9a2 2 0 012-2h12a2 2 0 012 2v3" />
      <path d="M24 22v8M20 26h8" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="mx-auto mb-3 h-12 w-12 text-csm-border-strong" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 10h32L28 24v12l-8 4V24L8 10z" />
      <path d="M34 34l8 8M42 34l-8 8" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="mx-auto mb-3 h-12 w-12 text-csm-success" stroke="currentColor" strokeWidth="1.5">
      <circle cx="24" cy="24" r="18" />
      <path d="M16 24l6 6 10-12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const VARIANT_ICON: Record<EmptyStateVariant, () => React.ReactNode> = {
  'no-data':   BriefcaseIcon,
  'no-match':  FilterIcon,
  'all-clear': CheckIcon,
};

export function EmptyState({ icon, title, description, action, variant }: EmptyStateProps) {
  const DefaultIcon = variant ? VARIANT_ICON[variant] : BriefcaseIcon;
  return (
    <div className="flex flex-col items-center py-8 text-center">
      {icon ?? <DefaultIcon />}
      <p className="text-csm-sm font-semibold text-csm-ink">{title}</p>
      {description && (
        <p className="mt-1 text-csm-xs text-csm-ink-soft">{description}</p>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
